import { useAuthStore } from "@/stores/auth-store";
import { hasPermission, hasBackendPermission, SIGNER_PAIRING_SCOPE, type Permission } from "@/lib/permissions";

/**
 * The permission check for the current session (ALL-5705).
 *
 * A loaded effective-access contract (`source === "api"`) is authoritative even when its
 * `grantedPermissions` list is empty: an explicit zero-grant answer means no permission, and the
 * static role table must never replace it (it used to, because the fallback keyed on the list being
 * empty, so an `organization_admin` role with zero loaded grants still enabled role-only controls).
 * Only explicitly labelled fixture sessions fall back to the generated fixture role
 * table; an unknown session fails closed; the API stays the authority for every request either way.
 */
function permissionCheck(
  experience: { source?: string; grantedPermissions?: string[] } | null | undefined,
  role: string | null | undefined,
): (permission: Permission) => boolean {
  const grantedPermissions = experience?.grantedPermissions ?? [];
  if (experience?.source === "api" || (experience?.source === "fixture" && grantedPermissions.length > 0)) {
    return (permission) => hasBackendPermission(grantedPermissions, permission);
  }
  return (permission) => experience?.source === "fixture" && hasPermission(role, permission);
}

export function usePermission(permission: Permission): boolean {
  const role = useAuthStore((s) => s.user?.role);
  const experience = useAuthStore((s) => s.experience);
  return permissionCheck(experience, role)(permission);
}

export function usePermissions(permissions: Permission[], mode: "any" | "all" = "any"): boolean {
  const role = useAuthStore((s) => s.user?.role);
  const experience = useAuthStore((s) => s.experience);
  const check = permissionCheck(experience, role);
  if (mode === "all") {
    return permissions.every(check);
  }
  return permissions.some(check);
}

/**
 * Checks only the loaded effective-access contract. Unlike `usePermission`,
 * this never falls back to the static role table while the contract is empty,
 * so an API-backed widget cannot issue a request before its real scope and
 * permission grants are known.
 */
export function loadedPermissionDecision(
  experience: { source: string; grantedPermissions: string[]; grantedScopes: string[] },
  permission: Permission,
  requiredScope?: string,
): { loaded: boolean; allowed: boolean } {
  const loaded = experience.source === "api";
  const permissionAllowed = loaded && hasBackendPermission(experience.grantedPermissions, permission);
  const scopeAllowed = !requiredScope || experience.grantedScopes.includes(requiredScope);
  return { loaded, allowed: permissionAllowed && scopeAllowed };
}

export function useLoadedPermission(permission: Permission, requiredScope?: string): { loaded: boolean; allowed: boolean } {
  const experience = useAuthStore((s) => s.experience);
  return loadedPermissionDecision(experience, permission, requiredScope);
}

export function useCurrentRole(): string | null {
  return useAuthStore((s) => s.user?.role ?? null);
}

/**
 * Whether this session may issue a signer pairing code (Admin > Users > user > "Pair a phone").
 *
 * The gateway gates POST /v1/signer/pairings on the scope `auth_roles:manage` plus a fresh AAL2
 * step-up (GatewayRouteScopes.requiredRoute, SignerPairingController), so the button follows that
 * exact scope from GET /v1/session/effective-access. It no longer follows `user:full`
 * (`admin.user.manage`), a permission real admin sessions do not carry, which hid the button from
 * every real admin. It fails closed: until the real contract has loaded (a fixture or unknown source)
 * the button stays hidden, since the card only ever calls the real API. The API remains the
 * authority (scope, fresh AAL2, human principal, same org); this only shows or hides the button.
 */
export function useSignerPairingAllowed(): boolean {
  const experience = useAuthStore((s) => s.experience);
  return experience.source === "api" && experience.grantedScopes.includes(SIGNER_PAIRING_SCOPE);
}
